import View from '../Support/View/View';
import morphdom from 'morphdom';
import debounce from '../Support/helpers/debounce';
import delegate from '../Support/helpers/delegate-it';

export default class ListingPageTemplate extends View {
    #filterBar            = null;
    #secondaryFilterBarEl = null;
    #CACHE                = new Map();

    static get viewName() {
        return 'listing-page-template';
    }
    static get viewType() {
        return 'template';
    }

    start() {
        this.#setFilterBarInstance();
        this.#setSecondaryFilterBarElement();
        this.#handleFilterCollapse();

        const onFilterBarSelectedHandler = debounce((e) => this.#onFilterBarSelected(e), 300);

        this.#filterBar?.addEventHandler('view:filter-bar.selected', onFilterBarSelectedHandler);

        this.#handleSecondaryFilterBar();
    }

    #setFilterBarInstance() {
        this.#filterBar = View.instances.get(this.$el.querySelector('[data-view-name="filter-bar"]')?.dataset?.viewId);
    }

    #setSecondaryFilterBarElement() {
        this.#secondaryFilterBarEl = this.$el.querySelector('.secondary-filter-bar');
    }

    /**
     * @param {CustomEvent} e
     */
    #onFilterBarSelected({ detail: { values } }) {
        const cacheKey = location.href;

        const afterItemListContentUpdated = () => {
            if (!this.#secondaryFilterBarEl) {
                this.#setSecondaryFilterBarElement();

                setTimeout(() => this.#handleSecondaryFilterBar(), 0);
            }
        };

        if (this.#CACHE.has(cacheKey)) {
            const cached = this.#CACHE.get(cacheKey);

            this.#updateItemListContent(cached.html, afterItemListContentUpdated);

            return;
        }

        Array.from(this.#filterBar.$el.elements).forEach(element => {
            element.setAttribute('aria-busy', true);
            element.setAttribute('disabled', true);
        });

        axios
            .post(location.href, {selector: values})
            .then(response => {
                this.#updateItemListContent(response.data, afterItemListContentUpdated);

                this.#CACHE.set(cacheKey, {html: response.data});
            })
            .catch(error => {
                if (this.#filterBar?.$el?.nodeName?.toLowerCase() === 'form') {
                    this.#filterBar?.$el?.submit();
                }
            });
    }

    /**
     * @param {string} DOMString 
     * @param {Function|null} callback 
     */
    #updateItemListContent(DOMString, callback = null) {
        const html = this.#makeHtmlFromString(DOMString);

        this.$el.querySelectorAll('.listing-page-template-content .card [data-view-name="svg"]').forEach(svg => svg.innerHTML = '');

        this.#morphdom('.listing-page-template-content', html);
        this.#refreshPolyfills();

        if (callback) {
            callback();
        }
    }

    /**
     * @param {string} DOMString 
     *
     * @returns {HTMLHtmlElement}
     */
    #makeHtmlFromString(DOMString) {
        const html = document.createElement('html');

        html.innerHTML = DOMString;

        return html;
    }

    /**
     * @param {string} selector
     * @param {HTMLHtmlElement} html
     */
    #morphdom(selector, html) {
        morphdom(this.$el.querySelector(selector), html.querySelector(selector));
    }

    #refreshPolyfills() {
        window.$polyfills?.objectFitImages();
    }

    #handleSecondaryFilterBar() {
        if (!this.#secondaryFilterBarEl) {
            return;
        }

        const pushHistoryState = (values, shapes, params) => {
            const state = {
                values: !values.length ? ['default'] : values,
                secondary: !shapes.length ? ['default'] : shapes,
            };

            history.pushState(state, '', `${location.origin}${location.pathname}?${params}`);
        }

        const onClick = ({ target }) => {
            const params   = new URLSearchParams(location.search);
            const selected = params.get('selected')?.split(',') || [];
            let secondary  = [];

            target.setAttribute('aria-busy', true);
            target.setAttribute('disabled', true);

            if (!target.classList.contains('is-active')) {
                secondary = target.dataset.id ? [target.dataset.id] : [];
            }

            params.set('shapes', secondary);

            pushHistoryState(selected, secondary, params);

            sendRequest(selected, secondary);
        };
        const sendRequest = (selector, secondary) => {
            axios
                .post(location.href, { selector, secondary })
                .then(response => {
                    this.#updateItemListContent(response.data);
                })
                .catch(error => location.reload());
        }
        const onHistoryPopState = ({ state }) => {
            if (!state?.values?.length) {
                return;
            }
            if (!state?.secondary?.length) {
                return;
            }

            sendRequest(state.values, state.secondary);
        }

        delegate(this.$el, '.secondary-filter-bar .btn', 'click', onClick);

        window.addEventListener('popstate', onHistoryPopState);
    }

    #handleFilterCollapse() {
        const collapse = this.$el.querySelector('.filter-collapse');

        if (!collapse) {
            return;
        }

        if (!collapse.open) {
            return;
        }

        const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);

        if (viewportWidth <= 640) { // "sm" screens
            collapse.open = false;
        }
    }
}